import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from 'lucide-react';
import { useId, type ReactElement } from 'react';
import { CampoSelect } from './campo-select';

export const TABLE_PAGE_SIZE_OPTIONS = [10, 30, 50, 100] as const;

interface TablePaginationProps {
  readonly page: number;
  readonly pageSize: number;
  readonly totalItems: number;
  readonly onPageChange: (page: number) => void;
  /** Obrigatório quando o seletor de linhas por página é exibido. */
  readonly onPageSizeChange?: (pageSize: number) => void;
  readonly itemLabel?: string;
  readonly canGoNext?: boolean;
  /** Esconde o seletor de linhas por página (tamanho de página fixo no servidor). */
  readonly hidePageSize?: boolean;
  /** `semantica`: só classes semânticas (tema do portal) e alvos de 44px abaixo de 1024px. */
  readonly appearance?: 'padrao' | 'semantica';
}

/** Rodapé único para grades operacionais, com alvos adequados para mouse e tablet. */
export function TablePagination({
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  itemLabel = 'itens',
  canGoNext,
  hidePageSize = false,
  appearance = 'padrao',
}: TablePaginationProps): ReactElement {
  const pageSizeId = useId();
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const firstItem = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastItem = Math.min(page * pageSize, totalItems);
  const nextEnabled = canGoNext ?? page < totalPages;
  const semantica = appearance === 'semantica';

  return (
    <nav
      aria-label="Paginação da tabela"
      className={
        semantica
          ? 'flex flex-col gap-2 border-t border-border bg-muted px-4 py-2 text-[length:var(--font-label)] text-muted-foreground sm:flex-row sm:items-center sm:justify-between'
          : 'flex flex-col gap-3 border-t bg-slate-50 px-3 py-2.5 text-xs text-slate-600 sm:flex-row sm:items-center sm:justify-between'
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        {hidePageSize ? null : (
          <>
            <label htmlFor={pageSizeId} className="font-medium">Linhas por página</label>
            <CampoSelect
              id={pageSizeId}
              aria-label="Linhas por página"
              size="sm"
              value={String(pageSize)}
              onValueChange={(valor) => onPageSizeChange?.(Number(valor))}
              options={TABLE_PAGE_SIZE_OPTIONS.map((option) => ({ value: String(option), label: String(option) }))}
              className={semantica ? 'w-20 text-[length:var(--font-label)] font-semibold max-lg:min-h-11' : 'w-[4.5rem] text-xs font-semibold text-slate-800'}
            />
          </>
        )}
        <span aria-live="polite">
          {firstItem}–{lastItem} de {totalItems} {itemLabel}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1">
        <span className={semantica ? 'mr-2 font-semibold' : 'mr-2 font-medium'}>Página {page} de {totalPages}</span>
        <PaginationButton semantica={semantica} label="Primeira página" disabled={page <= 1} onClick={() => onPageChange(1)}>
          <ChevronFirst className="h-4 w-4" />
        </PaginationButton>
        <PaginationButton semantica={semantica} label="Página anterior" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
          <ChevronLeft className="h-4 w-4" />
        </PaginationButton>
        <PaginationButton semantica={semantica} label="Próxima página" disabled={!nextEnabled} onClick={() => onPageChange(page + 1)}>
          <ChevronRight className="h-4 w-4" />
        </PaginationButton>
        <PaginationButton
          semantica={semantica}
          label="Última página"
          disabled={!nextEnabled || canGoNext !== undefined}
          onClick={() => onPageChange(totalPages)}
        >
          <ChevronLast className="h-4 w-4" />
        </PaginationButton>
      </div>
    </nav>
  );
}

interface PaginationButtonProps {
  readonly semantica: boolean;
  readonly label: string;
  readonly disabled: boolean;
  readonly onClick: () => void;
  readonly children: ReactElement;
}

function PaginationButton({ semantica, label, disabled, onClick, children }: PaginationButtonProps): ReactElement {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={
        semantica
          ? 'inline-flex size-11 items-center justify-center rounded-md border border-border bg-card text-foreground hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40 lg:size-10'
          : 'inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40'
      }
    >
      {children}
    </button>
  );
}
